// Package Order Status: 1=temp, 2=order completed - awaiting payment, 3=payment received
export const PACKAGE_STATUS_TEMP = 1;
export const PACKAGE_STATUS_ORDERED = 2;
export const PACKAGE_STATUS_PAID = 3;
export const PACKAGE_STATUS_COMPLETED = 4;

/** Day orders on/after this date use UserMealPackage.siblingDiscount for [PD] labels; earlier days use paid-price comparison. */
export const PACKAGE_SIBLING_LABEL_FLAG_FROM_DAY = '2026_08_01';

// Basket checkout session status (users/{uid}/orderCheckout).
// Awaiting payment and/or order registration.
export const ORDER_CHECKOUT_STATUS_ORDERED = 'ORDERED';
// Some days committed, some failed — remaining days retry with credits.
export const ORDER_CHECKOUT_STATUS_PARTIAL = 'PARTIAL';
/**
 * A fulfillment run (placeBasketOrders / placeOrderPackage) older than this is considered stale and can be taken over.
 * A healthy run finishes in a few seconds and the callable itself times out at 60s, so a lock this old means the
 * holder died (e.g. vivaSuccess OOM-killed mid-fulfillment, Sep 2026). Client retries (`FULFILLMENT_RETRY_BUDGET_MS`)
 * are sized to outlast this so the next attempt takes over instead of surfacing "already being registered".
 */
export const ORDER_CHECKOUT_PROCESSING_STALE_MS = 60 * 1000;
/**
 * While a multi-day basket is registering, refresh `orderCheckout/processingAt` at least this often
 * so webhook + vivaSuccess cannot both take the lock (stale at 60s while day 6+ is still running).
 */
export const ORDER_CHECKOUT_LOCK_TOUCH_MS = 15 * 1000;
/**
 * Pending wallet idempotency marker older than this may be reclaimed after a crash between reserve and debit.
 */
export const CHECKOUT_WALLET_MARKER_PENDING_STALE_MS = 5 * 60 * 1000;
/** How long the order app keeps retrying paid fulfillment (mutex / webhook-timing / transport errors) before giving up. */
export const FULFILLMENT_RETRY_BUDGET_MS = ORDER_CHECKOUT_PROCESSING_STALE_MS + 20 * 1000;
/** prepareOrderPayment mutex (paymentInitAt) — holder is stale after this if it died mid-flow. */
export const VIVA_PAYMENT_INIT_STALE_MS = 30 * 1000;

/** Package temps (TEMP or ORDERED) older than this are cleared on the Packages screen and rejected server-side. */
export const PACKAGE_TEMP_STALE_MS = 7 * 24 * 60 * 60 * 1000;

/** Extra calendar days subtracted with orderDays when hiding old stale-basket rows (0 = none). See getBasketStaleAttentionFloorDay. */
export const BASKET_STALE_ATTENTION_BUFFER_DAYS = 0;

/** Warn when a payable basket entry is within this many ms of its checkout deadline (server time). */
export const CHECKOUT_DEADLINE_URGENCY_MS = 30 * 60 * 1000;

/**
 * After payment, basket fulfillment evaluates order deadlines as if the clock were this many ms earlier —
 * covers Viva redirect + parent completing payment shortly after the published cutoff (initiate still strict).
 * Paid fulfillment also re-checks at {@link OrderCheckoutSession.createdAt} so a late webhook/sweeper does not drop days that were quoted in time.
 */
export const CHECKOUT_FULFILLMENT_DEADLINE_GRACE_MS = 10 * 60 * 1000;

/** Order app platform support form (`submitOrderPlatformSupport`). */
export const PLATFORM_SUPPORT_MESSAGE_MIN_LENGTH = 10;
export const PLATFORM_SUPPORT_MESSAGE_MAX_LENGTH = 2000;

export const PLATFORM_SUPPORT_REASON_IDS = [
    'technical',
    'signIn',
    'payment',
    'ordersPackages',
    'other',
] as const;

export type PlatformSupportReasonId = (typeof PLATFORM_SUPPORT_REASON_IDS)[number];

/** Order app platform support — screenshot attachments (re-encoded JPEG on client). */
export const PLATFORM_SUPPORT_SNAPSHOT_MAX_COUNT = 3;
/** Max size of the file the user selects before client re-encode. */
export const PLATFORM_SUPPORT_SNAPSHOT_PICK_MAX_BYTES = 10 * 1024 * 1024;
/** Max stored JPEG size after upload (server rejects on submit). */
export const PLATFORM_SUPPORT_SNAPSHOT_STORED_MAX_BYTES = 5 * 1024 * 1024;
/** GCS V4 signed read URL maximum lifetime (seven days). */
export const PLATFORM_SUPPORT_SNAPSHOT_READ_URL_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * `sweepPaidCheckouts` (scheduled safety net for Viva payments that never reached vivaSuccess/webhook, or reached
 * them but the basket/package was not registered — e.g. instance OOM-killed mid-fulfillment, Sep 2026).
 */
/** How far back the sweeper looks at `fundActivity/viva` transactions (by `timeCreated`). */
export const PAID_CHECKOUT_SWEEP_LOOKBACK_MS = 48 * 60 * 60 * 1000;
/** Cloud Scheduler cadence for `sweepPaidCheckouts` (Firebase `onSchedule` English phrase). */
export const PAID_CHECKOUT_SWEEP_SCHEDULE = 'every 30 minutes';
/** Leave brand-new transactions to the live paths (user is still on the Viva page / client is retrying). */
export const PAID_CHECKOUT_SWEEP_MIN_AGE_MS = 3 * 60 * 1000;
/** A Paid checkout still PARTIAL this long after payment gets a manual-review entry (+ email) with a wallet ledger audit. */
export const PAID_CHECKOUT_SWEEP_FLAG_AFTER_MS = 20 * 60 * 1000;
/** Max ledger rows inspected by the wallet ledger chain audit attached to an `unfulfilled` review entry. */
export const PAID_CHECKOUT_SWEEP_LEDGER_ROWS = 30;
/**
 * How often an UNVERIFIED (never Paid in our DB) order is re-checked with Viva, by age. Abandoned checkouts stay
 * Pending at Viva for a long time — without pacing each one would cost ~290 getOrder calls over the 48 h window.
 * `[maxAgeMs, minIntervalMs]`, first matching tier wins; ages beyond the last tier use its interval.
 */
export const PAID_CHECKOUT_SWEEP_POLL_TIERS: ReadonlyArray<readonly [maxAgeMs: number, minIntervalMs: number]> = [
    [60 * 60 * 1000, 0],                     // first hour: every sweep run — catches "closed the tab" payments fast
    [6 * 60 * 60 * 1000, 30 * 60 * 1000],    // 1–6 h: every 30 min
    [Number.POSITIVE_INFINITY, 2 * 60 * 60 * 1000], // older: every 2 h
];
/** Client keeps retrying payment mutex / transport→lock compound errors until after the lock can expire. */
export const PAYMENT_PREP_RETRY_BUDGET_MS = VIVA_PAYMENT_INIT_STALE_MS + 10 * 1000;
/** HttpsError `details.checkoutAction` — client should run placeBasketOrders, not re-initiate checkout. */
export const CHECKOUT_ACTION_RESUME_FULFILLMENT = 'resumeFulfillment';
/** HttpsError `details.checkoutAction` — client should offer to remove `expiredDates` from the basket and retry checkout. */
export const CHECKOUT_ACTION_REMOVE_EXPIRED_DAYS = 'removeExpiredDays';

/** Days before package end to show “ending soon” warning in parent UI. */
export const NUM_DAYS_SUBSCRIPTION_WARNING = 5;

export const SCHOOL_TYPE_NURSERY = 'nursery';
export const SCHOOL_TYPE_PRIMARY = 'primary';
export const SCHOOL_TYPE_SECONDARY = 'secondary';

export const PORTION_SIZE_SMALL = 1;
export const PORTION_SIZE_MEDIUM = 2;
export const PORTION_SIZE_NORMAL = 3;

// Display-only constant: determines which date to show by default in UI (today until this hour, then tomorrow)
// Does NOT affect actual ordering deadlines - those are controlled by modelConstants.orderTime in database
export const SHOW_ORDER_TIME = 17;

export const MAX_SNACK_ITEMS = 9;

export const MAX_MEAL_ITEMS = 9;

/** Exclusive upper bound for wallet top-up (EUR): valid amounts are `> 0` and `< TOPUP_MAX_AMOUNT`. */
export const TOPUP_MAX_AMOUNT = 3000;

/** Exclusive upper bound for basket / package Viva payments (EUR): valid amounts are `> 0` and `< ORDER_PAYMENT_MAX_AMOUNT`. */
export const ORDER_PAYMENT_MAX_AMOUNT = 15000;

// Threshold for consecutive days without menu before stopping package processing
export const CONSECUTIVE_DAYS_WITHOUT_MENU_THRESHOLD = 20;

export const STATUS_CANCELLED = 'cancelled';

export const KEY_ORDER = 'order';
export const KEY_MAX_QUANTITY_LOCAL = 'maxQuantityLocal';

// Last read timestamp key for tables
export const ADMIN_LAST_READ_CONTACT = 'contact';
export const ADMIN_LAST_READ_PLATFORM_SUPPORT = 'platformSupportContact';
/** `contactForm` RTDB `from` value for order-app platform support tickets. */
export const CONTACT_FORM_FROM_ORDER_APP_PLATFORM_SUPPORT = 'orderAppPlatformSupport';
export const ADMIN_LAST_READ_FEEDBACK = 'feedback';
export const ADMIN_LAST_READ_VIVA_SUCCESS_MANUAL_REVIEW = 'vivaSuccessManualReview';
export const ADMIN_LAST_READ_SWEEPER_ACTIONS = 'sweeperActions';
export const ADMIN_LAST_READ_PAID_FULFILLMENT_FAILURES = 'paidFulfillmentFailures';
export const ADMIN_LAST_READ_PAYMENT_FINALIZE_FAILURES = 'paymentFinalizeFailures';
export const ADMIN_LAST_READ_WALLET_CHECKOUT_INCIDENTS = 'walletCheckoutIncidents';
/** Default start date (Europe/Athens midnight) for admin checkout-incident historical backfill. */
export const CHECKOUT_INCIDENT_BACKFILL_DEFAULT_SINCE_DATE = '2026-08-25';
/** `adminRunCheckoutIncidentBackfill` + admin client callable timeout (full-range scan can exceed 9 min). */
export const CHECKOUT_INCIDENT_BACKFILL_TIMEOUT_SECONDS = 1800;

// Combo configuration constants
export const COMBO_DONT_USE: number | null = null;
export const COMBO_ALWAYS = 1;
export const COMBO_WHEN_ALL_SELECTED = 2;
export const COMBO_WHEN_ANY_SELECTED = 3;

export const catering = {
    Demo: 'Demo',
    Gefsinus: 'Gefsinus'
};
export const menu = {
    Demo: 'Demo',
    Gefsinus1: 'Gefsinus1',
    Gefsinus2: 'Gefsinus2',
    Gefsinus3: 'Gefsinus3',
    Gefsinus4: 'Gefsinus4',
    Gefsinus5: 'Gefsinus5',
    Gefsinus6: 'Gefsinus6',
    Gefsinus7: 'Gefsinus7',
    Gefsinus8: 'Gefsinus8',
    Gefsinus9: 'Gefsinus9'
};

export const dailyMeals = {
    Demo: 'Demo',
    Ac: 'Ac',
    StcL: 'StcL',
    StcU: 'StcU',
    StcSC1: 'StcSC1',
    StcSC2: 'StcSC2',
    AcsL: 'AcsL',
    AcsU: 'AcsU',
    Campion: 'Campion',
    MoraitisL: 'MoraitisL',
    MoraitisU: 'Moraitis'
};

/**
 * Convert package status number to human-readable text
 * @param status - The package status number
 * @returns The status text label
 */
export function getPackageStatusText(status: number): string {
    switch (status) {
        case PACKAGE_STATUS_TEMP: return 'Temp';
        case PACKAGE_STATUS_ORDERED: return 'Ordered';
        case PACKAGE_STATUS_PAID: return 'Paid';
        case PACKAGE_STATUS_COMPLETED: return 'Completed';
        default: return 'Unknown';
    }
}

/**
 * List of demo user IDs that should be excluded from various admin reports and statistics
 * These are test/demo accounts that shouldn't be counted in production metrics
 */
export const demoUsers = [
    'QfjHjv6iaCNSG5Lz58we85yb2v93',  // pannikdouk
    'HKUooD36VxdpeHaUycBNBysbPRX2', // Zois Parthymos
    'TqW4kDC6pNbeA5QifkHdvsxcYoP2', // Georgia Belekou
    '1FKvobijxvSYnWnIeWyRPqRxgEI3', // Marianna Angelopoulou
    'lCIGH0gVLzNELD181THVYxX3lrj2', // NikiFo Yahoo
    'JkYhM4S1MyW8hI3Njn1KKQI0mjn2', // c@c.com
    'Tc59bLWXnfWTtXZgFxIvK7kKExV2', // a@a.com
    'nacn0vrnD3fZ9STAcySR6KtNr442', // d@d.com
    'HuqzVYRfe9MMPZQzh1bCdayApAx1' // b@b.com
];

export const cateringTestUsers = [
    'sZZXyv3cpSUAPTGDqgetvb0ez3r2', // Marilena Loulaki
    '7y6M2xc2HeOp9iBrH3S1vv0bLeN2', // Athens college
    'QOrsiISOsCNsMUlYHa2OLgP7e933',  // Γιώργος Μπόρσης
    'rXZQVAGOFFbdpEiZJW2jmfLWjg72'  // St Catherine's 
];

/**
 * Checks if a user should be included in admin views based on admin permissions
 * @param userId - The user ID to check
 * @param hasAdminPermissions - Whether the current user has admin permissions
 * @returns boolean indicating if the user should be included
 */
export function shouldIncludeUser(userId: string, hasAdminPermissions: boolean): boolean {
    // If user has admin permissions, show all users including demo users
    if (hasAdminPermissions) return true;

    // If user doesn't have admin permissions, exclude demo users
    return !demoUsers.includes(userId);
}